import React, { useEffect, useState } from 'react';
import Modal from '../../../../../shared/design-system/components/atoms/modal';
import { BULK_SEARCH_FILTERS } from './config';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import { BulkFilters } from './type';
import MultiChipInput from '../../../../../shared/components/multi-chip-input';
import {
  getIsErrorFromErrorMapping,
  getMappedValueFromKey,
} from './helper/bulk-search-helper';
import { getCompanyNameByIdsAPI } from '../../../helpers/leads.api';
import { isPositiveCompanyId } from '../../../helpers/helper';

type IProps = {
  show: boolean;
  onClose: () => void;
  onSearch: (bulkFilters: BulkFilters) => void;
  focusKey?: string;
  pendingFilters: URLSearchParams;
};

const LeadsFilterBulkSearch: React.FC<IProps> = ({
  show,
  onClose,
  onSearch,
  focusKey,
  pendingFilters,
}) => {
  const [bulkFilters, setBulkFilters] = useState<BulkFilters>({});
  const [bulkFilterError, setBulkFilterError] = useState<
    Record<string, string>
  >({});
  const [companyIdNameMap, setCompanyIdNameMap] = useState<
    Record<string, string>
  >({});
  const [isResolvingCompanyIds, setIsResolvingCompanyIds] = useState<boolean>(
    false,
  );

  const isError = getIsErrorFromErrorMapping(bulkFilterError);

  // Resolve company_ids to company names so modal chips show names (not IDs)
  useEffect(() => {
    if (!show) return;
    // Only needed when Company Name bulk input is visible
    const companyNameIsVisible =
      (focusKey && focusKey === 'company_name') ||
      (!focusKey && BULK_SEARCH_FILTERS.some((f) => f.key === 'company_name'));
    if (!companyNameIsVisible) return;

    const ids = pendingFilters
      .getAll('company_ids')
      .map((v) => v.replace(/"/g, '').trim())
      .filter((v) => v.length > 0)
      .filter((v) => isPositiveCompanyId(v));
    const idsToFetch = ids.filter((id) => !companyIdNameMap[id]);
    if (idsToFetch.length === 0) return;

    let isCancelled = false;
    (async () => {
      setIsResolvingCompanyIds(true);
      try {
        const res = await getCompanyNameByIdsAPI({
          companyIds: idsToFetch.map((id) => Number(id)),
        });
        const payload = (res as any)?.payload;
        if (!Array.isArray(payload)) return;
        const nextMap: Record<string, string> = {};
        payload.forEach((c: any) => {
          const id =
            c?.companyId ?? c?.company_id ?? c?.id ?? c?.companyID ?? null;
          const name =
            c?.companyName ??
            c?.company_name ??
            c?.name ??
            c?.companyNAME ??
            null;
          if (id != null && typeof name === 'string' && name.trim() !== '') {
            nextMap[String(id)] = name.trim();
          }
        });
        if (!isCancelled && Object.keys(nextMap).length > 0) {
          setCompanyIdNameMap((prev) => ({ ...prev, ...nextMap }));
        }
      } catch (_e) {
        // ignore and fall back to IDs
      } finally {
        if (!isCancelled) {
          setIsResolvingCompanyIds(false);
        }
      }
    })();

    // eslint-disable-next-line consistent-return
    return () => {
      isCancelled = true;
    };
  }, [show, focusKey, pendingFilters, companyIdNameMap]);

  const updateBulkFiltersFromPendingFilters = () => {
    const updatedBulkFilters: BulkFilters = {};
    const filtersToShow = focusKey
      ? BULK_SEARCH_FILTERS.filter((f) => f.key === focusKey)
      : BULK_SEARCH_FILTERS;

    filtersToShow.forEach(({ key, checkboxDataList }) => {
      let queryValues: string[] = [];
      if (key === 'company_name') {
        const names = pendingFilters.getAll('company_name');
        const idsOrNames = pendingFilters
          .getAll('company_ids')
          .map((id) => id.replace(/"/g, '').trim())
          .filter(Boolean);

        const typedCompanyNames = idsOrNames.filter(
          (v) => !isPositiveCompanyId(v),
        );
        const idsAsNames = idsOrNames
          .filter((v) => isPositiveCompanyId(v))
          .map((id) => companyIdNameMap[id])
          .filter(Boolean) as string[];

        queryValues = [...names, ...typedCompanyNames, ...idsAsNames];
      } else {
        queryValues = pendingFilters.getAll(key);
      }

      const showExactMatch =
        pendingFilters.get(`${key}_exactMatch`) === '1' ||
        (key === 'company_name' &&
          pendingFilters.get('company_ids_exactMatch') === '1');

      queryValues = queryValues.map((value) => value.replace(/"/g, ''));

      if (checkboxDataList) {
        // Considering values with checkboxDataList won't have show exact match
        queryValues = queryValues.map(
          (value) => getMappedValueFromKey(checkboxDataList, value) ?? value,
        );
      }

      updatedBulkFilters[key] = {
        values: queryValues.filter((v) => v.trim().length > 0),
        showExactMatch,
      };
    });
    setBulkFilters(updatedBulkFilters);
  };

  useEffect(() => {
    if (show) {
      updateBulkFiltersFromPendingFilters();
    }
  }, [show, pendingFilters, focusKey, companyIdNameMap]);

  const onCheckExactMatch = (key: string, checked: boolean) => {
    setBulkFilters({
      ...bulkFilters,
      [key]: {
        ...(bulkFilters[key] ? bulkFilters[key] : { values: [] }),
        showExactMatch: checked,
      },
    });
  };
  const onValueChange = (key: string, value: string[]) => {
    setBulkFilters({
      ...bulkFilters,
      [key]: {
        ...(bulkFilters[key] ? bulkFilters[key] : { showExactMatch: false }),
        values: value,
      },
    });
  };

  const onSubmit = () => {
    onSearch(bulkFilters);
  };

  const filtersToRender = focusKey
    ? BULK_SEARCH_FILTERS.filter((f) => f.key === focusKey)
    : BULK_SEARCH_FILTERS;

  const hasAnyValue = filtersToRender.some(({ key }) =>
    (bulkFilters[key]?.values ?? []).some((v) => v.trim().length > 0),
  );

  return (
    <Modal
      show={show}
      titleContent="Bulk Search"
      showCloseIcon={true}
      submitButtonText="Search"
      onHide={onClose}
      onClose={onClose}
      onSubmit={onSubmit}
      isSubmitDisabled={isError || !hasAnyValue}
      className="v2-leads-filter-bulk-search"
    >
      {filtersToRender.map(
        ({
          key,
          icon,
          labelText,
          placeholder,
          showExactMatchCheckbox,
          maxInputCount,
        }) => (
          <div key={key}>
            <div className="search-input-label-container">
              <div className="search-input-label">
                <div className="label-icon">{icon}</div>
                <div className="label">{labelText}</div>
              </div>
              {showExactMatchCheckbox ? (
                <Checkbox
                  value="filterKey"
                  className="show-exact-match-check"
                  onChange={(checked) => onCheckExactMatch(key, checked)}
                  checked={bulkFilters[key]?.showExactMatch}
                  label={
                    <div className="label-container d-inline-block">
                      <p className="checkbox-label-text m-0">
                        Show Exact Match
                      </p>
                    </div>
                  }
                />
              ) : null}
            </div>
            <MultiChipInput
              values={bulkFilters[key]?.values ?? []}
              onChange={(value) => onValueChange(key, value)}
              placeholder={placeholder}
              showInputCount={true}
              maxInputCount={maxInputCount}
              isLoading={key === 'company_name' && isResolvingCompanyIds}
              error={bulkFilterError?.[key]}
              onError={(error) => {
                setBulkFilterError((prevError) => ({
                  ...prevError,
                  [key]: error,
                }));
              }}
              scrollTo={focusKey === key}
            />
          </div>
        ),
      )}
    </Modal>
  );
};

export default LeadsFilterBulkSearch;
